import { useState } from 'react';
import { useSyncExternalStore } from 'react';
import { Badge, Button, Modal } from '../ui/kit';
import { outbox, type OutboxState } from './outbox';

export const useOutbox = (): OutboxState => useSyncExternalStore(outbox.subscribe, outbox.getSnapshot);

const ago = (t: number) => new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/**
 * Says plainly when the connection is down, what is waiting to be sent, and anything the server refused when it was
 * sent. Refused changes stay in the list until someone dismisses them: nothing disappears quietly.
 */
export function OfflineBar() {
  const s = useOutbox();
  const [open, setOpen] = useState(false);
  const n = s.waiting.length;
  const bad = s.failed.length;
  if (s.online && n === 0 && bad === 0) return null;

  return (
    <>
      <div className={`banner ${s.online && n === 0 ? 'bad' : ''}`} role="status" data-testid="offline-bar">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span>
            {!s.online ? 'No connection. ' : ''}
            {!s.online && n === 0 ? 'You can keep looking at what is on screen. Taking payment and other new actions need a connection.' : null}
            {n > 0 ? `${n} change${n === 1 ? ' is' : 's are'} saved on this device and ${s.online ? 'being sent now' : 'will be sent when the connection returns'}. ` : null}
            {bad > 0 ? `${bad} change${bad === 1 ? ' was' : 's were'} refused and need${bad === 1 ? 's' : ''} a look.` : null}
          </span>
          {n + bad > 0 ? (
            <Button variant="ghost" onClick={() => setOpen(true)}>
              See them
            </Button>
          ) : null}
        </div>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="Changes waiting to be sent">
        <div className="stack">
          {s.waiting.map((w) => (
            <div key={w.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{w.label}</span>
                <Badge tone="warn">Waiting</Badge>
              </div>
              <span className="small muted">Made at {ago(w.createdAt)}</span>
            </div>
          ))}
          {s.failed.map((f) => (
            <div key={f.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{f.label}</span>
                <Badge tone="bad">Refused</Badge>
              </div>
              <span className="small muted">{f.error}</span>
              <span className="hint">This was not done. Check the screen, and do it again if it is still needed.</span>
              <div className="row">
                <Button onClick={() => outbox.retry(f.id)}>Try again</Button>
                <Button variant="ghost" onClick={() => outbox.dismiss(f.id)}>
                  Dismiss
                </Button>
              </div>
            </div>
          ))}
          {n + bad === 0 ? <p className="small muted">Nothing is waiting.</p> : null}
          <Button block onClick={() => setOpen(false)}>
            Close
          </Button>
        </div>
      </Modal>
    </>
  );
}
